import React, { useState, useEffect, useRef } from "react";
import { Button, Box, Paper, Typography, IconButton, Tooltip, Collapse, useTheme } from "@mui/material";
import { KeyboardArrowUp, KeyboardArrowDown, DragIndicator, InfoOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

interface Step {
    id: string;
    text: string;
}

interface SortTheParagraphMobileProps {
    htmlContent: string;
    onSubmit: () => void;
    onVerify?: (result: boolean) => void;
    onSaveProgress?: (data: { placedElements?: Record<string, string[]>; selectedOptions?: string[]; points: number }) => void;
    data?: { placedElements?: Record<string, string[]>; selectedOptions?: string[]; points: number };
}

interface MobileSortableItemProps {
    step: Step;
    index: number;
    totalSteps: number;
    moveStep: (fromIndex: number, toIndex: number) => void;
    isCorrect: boolean | null;
    hasChecked: boolean;
    stepFeedback: Record<string, "correct" | "incorrect" | null>;
    onDragStart: (index: number, startY: number) => void;
    onDragMove: (currentY: number) => void;
    onDragEnd: () => void;
    isDraggedItem: boolean;
    isDropTarget: boolean;
    draggedOverIndex: number | null;
}

const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
};

const triggerHapticFeedback = (type: 'light' | 'medium' | 'heavy' = 'medium') => {
    if ('vibrate' in navigator) {
        const patterns = {
            light: [10],
            medium: [20],
            heavy: [50]
        };
        navigator.vibrate(patterns[type]);
    }
};

const SortTheParagraphMobile: React.FC<SortTheParagraphMobileProps> = ({ 
    htmlContent, 
    onVerify, 
    onSubmit, 
    onSaveProgress, 
    data
}) => {
    const theme = useTheme();
    const parsedContent = typeof htmlContent === "string" ? JSON.parse(htmlContent) : htmlContent;
    const correctOrder = parsedContent.correct_order || [];
    const [feedback, setFeedback] = useState<string | null>(null);
    const [hasChecked, setHasChecked] = useState<boolean>(false);
    const [showInstructions, setShowInstructions] = useState(false);
    
    const { t } = useTranslation();
    
    const [stepFeedback, setStepFeedback] = useState<Record<string, "correct" | "incorrect" | null>>({});
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [isCorrect, setIsCorrect] = useState<boolean | null>(submitted ? true : null);
    const [earnedPoints, setEarnedPoints] = useState(0);
    
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [draggedOverIndex, setDraggedOverIndex] = useState<number | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStartY, setDragStartY] = useState(0);
    const [currentDragY, setCurrentDragY] = useState(0);
    const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
    const itemRefs = useRef<(HTMLElement | null)[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);
    const originalScrollY = useRef<number>(0);
    
    const [steps, setSteps] = useState<Step[]>(() => {
        if (data?.placedElements && Object.keys(data.placedElements).length > 0) {
            setHasChecked(true);
            setEarnedPoints(data.points || 0);
            return Object.values(data.placedElements)
                .flat()
                .map(stepId => parsedContent.steps.find((step: Step) => step.id === stepId))
                .filter((step): step is Step => step !== undefined);
        }
        return shuffleArray(parsedContent.steps) || [];
    });
    
    useEffect(() => {
        const preventDefault = (e: Event) => {
            if (isDragging) {
                e.preventDefault();
                e.stopPropagation();
            }
        };

        const preventTouchMove = (e: TouchEvent) => {
            if (isDragging) {
                e.preventDefault();
            }
        };

        if (isDragging) {
            originalScrollY.current = window.pageYOffset;
            document.body.style.position = 'fixed';
            document.body.style.top = `-${originalScrollY.current}px`;
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden';
            
            document.addEventListener('touchmove', preventTouchMove, { passive: false });
            document.addEventListener('touchstart', preventDefault, { passive: false });
            document.addEventListener('scroll', preventDefault, { passive: false });
            document.addEventListener('wheel', preventDefault, { passive: false });
        } else {
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            document.body.style.overflow = '';
            
            window.scrollTo(0, originalScrollY.current);
            
            document.removeEventListener('touchmove', preventTouchMove);
            document.removeEventListener('touchstart', preventDefault);
            document.removeEventListener('scroll', preventDefault);
            document.removeEventListener('wheel', preventDefault);
        }

        return () => {
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            document.body.style.overflow = '';
            document.removeEventListener('touchmove', preventTouchMove);
            document.removeEventListener('touchstart', preventDefault);
            document.removeEventListener('scroll', preventDefault);
            document.removeEventListener('wheel', preventDefault);
        };
    }, [isDragging]);

    // Data loading effect
    useEffect(() => {
        if (!parsedContent?.steps) return; 
        if (!data || !data.placedElements || typeof data.placedElements !== "object") {
            return;
        }
        
        const placedElementsArray = Object.values(data.placedElements).flat();
        if (placedElementsArray.length > 0) {
            setSteps(
                placedElementsArray
                    .map(stepId => parsedContent.steps.find((step: Step) => step.id === stepId))
                    .filter((step): step is Step => step !== undefined)
            );
            
            const isCorrectlyOrdered = JSON.stringify(placedElementsArray) === JSON.stringify(correctOrder);
            const newStepFeedback: Record<string, "correct" | "incorrect" | null> = {};
            steps.forEach((step, index) => {
                newStepFeedback[step.id] = step.id === correctOrder[index] ? "correct" : "incorrect";
            });
            setStepFeedback(newStepFeedback);

            if (isCorrectlyOrdered) {
                setSubmitted(true);
                setIsCorrect(true);
                setHasChecked(true);
                setFeedback(parsedContent.feedback.correct);
            } else {
                setIsCorrect(false);
                setFeedback(parsedContent.feedback.incorrect);
            }
        }
    }, [data, parsedContent]);

    const moveStep = (fromIndex: number, toIndex: number) => {
        if (hasChecked || fromIndex === toIndex) return;
        
        const updatedSteps = [...steps];
        const [movedStep] = updatedSteps.splice(fromIndex, 1);
        updatedSteps.splice(toIndex, 0, movedStep);
        setSteps(updatedSteps);
        triggerHapticFeedback('light');
    };

    const handleVerify = () => {
        if (hasChecked) return; 

        const currentOrder = steps.map(step => step.id);
        const isCorrectAnswer = JSON.stringify(currentOrder) === JSON.stringify(correctOrder);

        const newFeedback: Record<string, "correct" | "incorrect" | null> = {};
        steps.forEach((step, index) => {
            newFeedback[step.id] = currentOrder[index] === correctOrder[index] ? "correct" : "incorrect";
        });

        setStepFeedback(newFeedback);
        setFeedback(isCorrectAnswer ? parsedContent.feedback.correct : parsedContent.feedback.incorrect);
        setIsCorrect(isCorrectAnswer);
        setHasChecked(true);
        const points = steps.filter((step, index) => step?.id === correctOrder[index]).length;
        setEarnedPoints(points);
        
        const placedElements = Object.fromEntries(
            steps.map((step, index) => [index.toString(), [step.id]])
        );
     
        setSubmitted(true);
        onSaveProgress?.({
            placedElements,
            points,
        });

        onSubmit();
        triggerHapticFeedback('heavy');
    };

    // Drag Handlers
    const handleDragStart = (index: number, startY: number) => {
        if (hasChecked) return;
        
        setDraggedIndex(index);
        setDragStartY(startY);
        setCurrentDragY(startY);
        
        longPressTimerRef.current = setTimeout(() => {
            setIsDragging(true);
            triggerHapticFeedback('medium');
        }, 200);
    };

    const handleDragMove = (currentY: number) => {
        if (!isDragging || draggedIndex === null) return;
        
        setCurrentDragY(currentY);
        
        const draggedOverIndex = calculateDraggedOverIndex(currentY);
        setDraggedOverIndex(draggedOverIndex);
    };

    const handleDragEnd = () => {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
        }
        
        if (isDragging && draggedIndex !== null && draggedOverIndex !== null && draggedIndex !== draggedOverIndex) {
            moveStep(draggedIndex, draggedOverIndex);
        }
        
        setDraggedIndex(null);
        setDraggedOverIndex(null);
        setIsDragging(false);
        setDragStartY(0);
        setCurrentDragY(0);
        
        if (isDragging) {
            triggerHapticFeedback('light');
        }
    };

    const calculateDraggedOverIndex = (currentY: number): number | null => {
        for (let i = 0; i < itemRefs.current.length; i++) {
            const element = itemRefs.current[i];
            if (element) {
                const rect = element.getBoundingClientRect();
                const middle = rect.top + rect.height / 2;
                if (currentY < middle) {
                    return i;
                }
            }
        }
        return steps.length - 1;
    };

    return (
        <Box sx={{
            padding: '12px 14px',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            fontFamily: theme.typography.fontFamily,
            '@media (max-width: 480px)': {
                padding: '10px 12px', 
            }
        }}>
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px'
            }}>
                <Typography 
                    variant="h5" 
                    sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        fontSize: '18px',
                        lineHeight: '1.4',
                        flex: 1,
                        textAlign: 'center',
                        '@media (max-width: 480px)': {
                            fontSize: '16px',
                        }
                    }}
                >
                    {parsedContent.question}
                </Typography>

                <Tooltip 
                    title={t("Show/hide instructions")}
                    placement="left"
                    arrow
                >
                    <IconButton
                        onClick={() => setShowInstructions(!showInstructions)}
                        sx={{
                            backgroundColor: showInstructions 
                                ? theme.palette.primary.main + '33' 
                                : theme.palette.primary.main + '1A',
                            color: theme.palette.primary.main,
                            width: '32px',
                            height: '32px',
                            marginLeft: '8px',
                            '&:hover': {
                                backgroundColor: theme.palette.primary.main + '40',
                                transform: 'scale(1.05)',
                            },
                            transition: 'all 0.2s ease',
                            '@media (max-width: 480px)': {
                                width: '28px',
                                height: '28px',
                                marginLeft: '6px',
                            }
                        }}
                    >
                        <InfoOutlined sx={{ 
                            fontSize: '18px',
                            '@media (max-width: 480px)': {
                                fontSize: '16px',
                            }
                        }} />
                    </IconButton>
                </Tooltip>
            </Box>

            <Collapse in={showInstructions}>
                <Box sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}1A, ${theme.palette.primary.main}0D)`,
                    border: `1px solid ${theme.palette.primary.main}4D`,
                    borderRadius: '4px',
                    padding: '10px',
                    marginBottom: '16px',
                    fontSize: '13px',
                    color: theme.palette.text.primary,
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                }}>
                    <DragIndicator sx={{ fontSize: '16px', color: theme.palette.primary.main }} />
                    <span>{t("Hold the grip icon and drag to sort")}</span>
                </Box>
            </Collapse>
            
            <Box 
                ref={containerRef}
                component="ul" 
                sx={{
                    listStyle: 'none',
                    padding: 0,
                    marginBottom: '20px',
                    position: 'relative',
                    touchAction: 'pan-y',
                }}
            >
                {steps.map((step, index) => (
                    <MobileSortableItem
                        key={step.id}
                        step={step}
                        index={index}
                        totalSteps={steps.length}
                        moveStep={moveStep}
                        isCorrect={isCorrect}
                        hasChecked={hasChecked}
                        stepFeedback={stepFeedback}
                        onDragStart={handleDragStart}
                        onDragMove={handleDragMove}
                        onDragEnd={handleDragEnd}
                        isDraggedItem={draggedIndex === index}
                        isDropTarget={draggedOverIndex === index && isDragging}
                        draggedOverIndex={draggedOverIndex}
                        ref={(el) => itemRefs.current[index] = el}
                    />
                ))}
            </Box>

            {!hasChecked && (
                <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: '14px' }}>
                    <Button
                        onClick={handleVerify}
                        variant="contained"
                        sx={{
                            backgroundColor: theme.palette.primary.main,
                            color: theme.palette.common.white,
                            borderRadius: '8px',
                            padding: '12px 32px',
                            fontSize: '16px',
                            fontWeight: '600',
                            textTransform: 'none',
                            minWidth: '160px',
                            boxShadow: `0 4px 16px ${theme.palette.primary.main}40`,
                            
                            '&:hover': {
                                backgroundColor: theme.palette.primary.main + 'CC',
                                boxShadow: `0 6px 20px ${theme.palette.primary.main}55`,
                                transform: 'translateY(-2px)',
                            },
                            
                            '@media (max-width: 480px)': {
                                fontSize: '15px',
                                padding: '10px 28px',
                                minWidth: '140px',
                            }
                        }}
                    >
                        {t("Überprüfen")}
                    </Button>
                </Box>
            )}

            {(hasChecked && feedback) && (
                <Box sx={{ 
                    textAlign: 'center',
                    padding: '16px',
                    backgroundColor: isCorrect 
                        ? theme.palette.success.main + '20' 
                        : theme.palette.error.main + '20',
                    borderRadius: '8px',
                    border: `1px solid ${isCorrect 
                        ? theme.palette.success.main + '40' 
                        : theme.palette.error.main + '40'}`,
                    marginTop: '14px'
                }}>
                    <Typography sx={{
                        color: isCorrect ? theme.palette.success.main : theme.palette.error.main,
                        fontWeight: '600',
                        fontSize: '16px',
                        marginBottom: '8px'
                    }}>
                        {t("Points earned")}: {earnedPoints} {t("of")} {steps.length}
                    </Typography>
                    {feedback && (
                        <Typography sx={{
                            color: isCorrect ? theme.palette.success.main : theme.palette.error.main,
                            fontSize: '14px',
                            fontWeight: '500'
                        }}>
                            {isCorrect ? t("Correct! Well done!") : t("Unfortunately incorrect. Try again!")}
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
};

const MobileSortableItem = React.forwardRef<HTMLLIElement, MobileSortableItemProps>(({ 
    step, 
    index, 
    totalSteps,
    moveStep, 
    isCorrect, 
    hasChecked, 
    stepFeedback,
    onDragStart,
    onDragMove,
    onDragEnd,
    isDraggedItem,
    isDropTarget,
    draggedOverIndex
}, ref) => {
    const theme = useTheme();
    const [isPressed, setIsPressed] = useState(false);

    const getColors = () => {
        if (stepFeedback[step.id] === "correct") {
            return {
                bg: theme.palette.success.main + '30',
                text: theme.palette.text.primary,
                handleColor: theme.palette.success.main + '99',
            };
        }
        if (stepFeedback[step.id] === "incorrect") {
            return {
                bg: theme.palette.error.main + '30',
                text: theme.palette.text.primary,
                handleColor: theme.palette.error.main + '99',
            };
        }
        return {
            bg: theme.palette.primary.main,
            text: theme.palette.common.white,
            handleColor: theme.palette.common.white + '99',
        };
    };

    const colors = getColors();

    const handleTouchStart = (e: React.TouchEvent) => {
        if (hasChecked) return;
        
        e.preventDefault();
        e.stopPropagation();
        setIsPressed(true);
        
        const touch = e.touches[0];
        onDragStart(index, touch.clientY);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (hasChecked || !isPressed) return;
        
        e.preventDefault();
        e.stopPropagation();
        
        const touch = e.touches[0];
        onDragMove(touch.clientY);
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        if (hasChecked) return;
        
        e.preventDefault();
        e.stopPropagation();
        setIsPressed(false);
        onDragEnd();
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        if (hasChecked) return;
        
        e.preventDefault();
        setIsPressed(true);
        onDragStart(index, e.clientY);
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (hasChecked || !isPressed) return;
        
        e.preventDefault();
        onDragMove(e.clientY);
    };

    const handleMouseUp = (e: React.MouseEvent) => {
        if (hasChecked) return;
        
        e.preventDefault();
        setIsPressed(false);
        onDragEnd();
    };

    const handleMoveUp = () => {
        if (index > 0) {
            moveStep(index, index - 1);
        }
    };

    const handleMoveDown = () => {
        if (index < totalSteps - 1) {
            moveStep(index, index + 1);
        }
    };

    return (
        <>
            {isDropTarget && (
                <Box sx={{
                    height: '3px',
                    backgroundColor: theme.palette.info.main,
                    borderRadius: '1px',
                    margin: '2px 0',
                    boxShadow: `0 0 8px ${theme.palette.info.main}80`,
                    animation: 'pulse 1s infinite',
                    '@keyframes pulse': {
                        '0%': { opacity: 0.5 },
                        '50%': { opacity: 1 },
                        '100%': { opacity: 0.5 },
                    }
                }} />
            )}
            
            <Paper
                ref={ref}
                component="li"
                sx={{
                    backgroundColor: colors.bg,
                    color: colors.text,
                    padding: '12px 16px',
                    margin: '6px 0',
                    borderRadius: '6px',
                    fontWeight: 600,
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                    height: '56px',
                    boxSizing: 'border-box',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: isDraggedItem ? 'scale(1.03) rotate(2deg)' : 'scale(1)',
                    boxShadow: isDraggedItem 
                        ? '0px 8px 20px rgba(0, 0, 0, 0.15)' 
                        : '0px 2px 4px rgba(0, 0, 0, 0.08)',
                    zIndex: isDraggedItem ? 1000 : 1,
                    opacity: isDraggedItem ? 0.9 : 1,
                    
                    '&:hover': !hasChecked ? {
                        backgroundColor: stepFeedback[step.id] === "correct" 
                            ? theme.palette.success.main + '40' 
                            : stepFeedback[step.id] === "incorrect"
                            ? theme.palette.error.main + '40'
                            : theme.palette.primary.main + 'CC',
                        transform: 'scale(1.01)',
                    } : {},
                    
                    '@media (max-width: 480px)': {
                        padding: '10px 12px',
                        fontSize: '13px',
                        height: '52px',
                        margin: '5px 0', 
                    }
                }}
            >
                <Box sx={{
                    flex: 1,
                    lineHeight: '1.4',
                    wordWrap: 'break-word',
                    userSelect: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    paddingRight: '8px',
                }}>
                    {step.text}
                </Box>

                {hasChecked && stepFeedback[step.id] && (
                    <Box sx={{
                        position: 'absolute',
                        right: '12px',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        backgroundColor: stepFeedback[step.id] === 'correct' 
                            ? theme.palette.success.main 
                            : theme.palette.error.main,
                        color: theme.palette.common.white,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        flexShrink: 0,
                        '@media (max-width: 480px)': {
                            right: '10px',
                            width: '18px',
                            height: '18px',
                            fontSize: '10px',
                        }
                    }}>
                        {stepFeedback[step.id] === 'correct' ? '✓' : '×'}
                    </Box>
                )}

                {!hasChecked && (
                    <Box sx={{
                        position: 'absolute',
                        right: '44px',
                        display: 'none',
                        gap: '3px',
                        flexShrink: 0,
                        '@media (max-width: 360px)': {
                            display: 'flex',
                        }
                    }}>
                        <IconButton
                            onClick={handleMoveUp}
                            disabled={index === 0}
                            size="small"
                            sx={{
                                width: '26px',
                                height: '26px',
                                backgroundColor: index === 0 ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.2)',
                                color: index === 0 ? 'rgba(255,255,255,0.5)' : colors.text,
                                '&:hover': index !== 0 ? {
                                    backgroundColor: 'rgba(255,255,255,0.3)',
                                } : {},
                            }}
                        >
                            <KeyboardArrowUp sx={{ fontSize: '15px' }} /> 
                        </IconButton>
                        
                        <IconButton
                            onClick={handleMoveDown}
                            disabled={index === totalSteps - 1}
                            size="small"
                            sx={{
                                width: '26px',
                                height: '26px',
                                backgroundColor: index === totalSteps - 1 ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.2)',
                                color: index === totalSteps - 1 ? 'rgba(255,255,255,0.5)' : colors.text,
                                '&:hover': index !== totalSteps - 1 ? {
                                    backgroundColor: 'rgba(255,255,255,0.3)',
                                } : {},
                            }}
                        >
                            <KeyboardArrowDown sx={{ fontSize: '15px' }} />
                        </IconButton>
                    </Box>
                )}

                {!hasChecked && (
                    <Box
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        sx={{
                            position: 'absolute',
                            right: '6px',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'grab',
                            borderRadius: '4px',
                            backgroundColor: isPressed ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                            transition: 'all 0.2s ease',
                            touchAction: 'none',
                            userSelect: 'none',
                            WebkitUserSelect: 'none',
                            msUserSelect: 'none',
                            flexShrink: 0,
                            
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                transform: 'scale(1.1)',
                            },
                            
                            '&:active': {
                                transform: 'scale(0.95)',
                            },
                            
                            '@media (max-width: 480px)': {
                                right: '4px',
                                width: '28px',
                                height: '28px',
                            },
                            
                            '@media (max-width: 360px)': {
                                display: 'none',
                            }
                        }}
                    >
                        <DragIndicator sx={{ 
                            fontSize: '18px',
                            color: colors.handleColor,
                            transition: 'color 0.2s ease',
                            pointerEvents: 'none',
                            '@media (max-width: 480px)': {
                                fontSize: '16px',
                            }
                        }} />
                    </Box>
                )}
            </Paper>
        </>
    );
});

MobileSortableItem.displayName = 'MobileSortableItem';

export default SortTheParagraphMobile;